import { useEffect, useRef, useState } from "react";
import { Star } from "lucide-react";
import { useT } from "@/i18n";

const Testimonials = () => {
  const t = useT(
    {
      eyebrow: "Kundenstimmen",
      heading: "Was meine Klient:innen sagen",
      intro: "Erfahrungen von Menschen, die den Weg zu mehr Wohlbefinden gegangen sind.",
      rating: "5,0 von 5 Sternen aus 22 Bewertungen",
      source:
        "Quelle: verifizierte Google-Rezensionen zur PRAXIS IB, Ärztezentrum West, 1140 Wien",
      score: "5,0",
      profileLink: "Alle Bewertungen auf Google ansehen",

    },
    {
      eyebrow: "Client voices",
      heading: "What my clients say",
      intro: "Experiences from people who have taken the path towards feeling better.",
      rating: "5.0 out of 5 stars from 22 reviews",
      source:
        "Source: verified Google reviews for PRAXIS IB, Ärztezentrum West, 1140 Vienna",
      score: "5.0",
      profileLink: "See all reviews on Google",
    },
  );
  const [isVisible, setIsVisible] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // Lazy load Elfsight script only when section is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { rootMargin: "200px" }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  // Load Elfsight script when section becomes visible
  useEffect(() => {
    if (isVisible && !scriptLoaded) {
      // Check if script already exists
      if (!document.querySelector('script[src*="elfsight.com/platform/platform.js"]')) {
        const script = document.createElement("script");
        script.src = "https://static.elfsight.com/platform/platform.js";
        script.async = true;
        document.head.appendChild(script);
      }
      setScriptLoaded(true);
    }
  }, [isVisible, scriptLoaded]);

  return (
    <section ref={sectionRef} id="rezensionen" className="py-12 md:py-16 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-8">
          <span className="text-primary font-medium text-sm uppercase tracking-wider">
            {t.eyebrow}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-4">
            {t.heading}
          </h2>
          <p className="text-lg text-muted-foreground">
            {t.intro}
          </p>
        </div>

        {/* Bewertungsübersicht mit Quellenangabe */}
        <div className="max-w-xl mx-auto mb-10">
          <div className="rounded-2xl border border-border bg-card shadow-sm px-6 py-5 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 text-center sm:text-left">
            <div className="flex flex-col items-center sm:items-start">
              <span className="text-4xl font-bold text-foreground leading-none">{t.score}</span>
              <div className="flex items-center gap-0.5 mt-2" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="h-5 w-5 fill-primary text-primary" />
                ))}
              </div>
            </div>
            <div className="sm:border-l sm:border-border sm:pl-6">
              <p className="text-foreground font-medium">
                {t.rating}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {t.source}
              </p>
              <a
                href="https://www.google.com/maps?q=Linzer+Stra%C3%9Fe+383,+1140+Wien"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-2 text-sm font-medium text-primary underline-offset-4 hover:underline"
              >
                {t.profileLink}
              </a>
            </div>

          </div>
        </div>


        {/* Elfsight Google Reviews Widget - loaded lazily */}
        <div className="max-w-6xl mx-auto">
          {isVisible && (
            <div 
              className="elfsight-app-8224ee42-64a7-48d2-a0d2-9633ea7eadaa" 
              data-elfsight-app-lazy
            ></div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
